'use client';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import Link from 'next/link';
import { SupportedLanguages } from '@/types/globals';
import { Menu } from '@/lib/fetchHeader';
import { generateLink } from '@/components/utils/getLinkTarget';
import MobileMenu from '@/components/layout/header/MobileMenu';
import SubMenu from '@/components/layout/header/SubMenu';
import ContentContainer from '@/components/layout/ContentContainer';
import ChevDown from '@/assets/icon/chev-down.svg';
import Image from 'next/image';
import HamburgerMenu from '@/assets/icon/hamburger-menu.svg';
import CloseIcon from '@/assets/icon/close-icon.svg';
import { useState, useEffect } from 'react';
import clsx from 'clsx';

export default function Header({
  lang,
  menu,
}: {
  lang: SupportedLanguages;
  menu: Menu;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    // Check initial scroll position on mount
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loginText = {
    en: {
      login: 'Login',
    },
    fr: {
      login: 'Se Connecter',
    },
  };

  return (
    <header
      className={clsx(
        'fixed top-0 right-0 left-0 z-20 flex min-h-8 justify-center transition-colors',
        isScrolled ? 'bg-qf-purple text-white' : 'bg-transparent text-white',
      )}
    >
      <ContentContainer
        additionalClassNames={'mx-auto flex w-full gap-6 pt-9 pb-4'}
      >
        <Link
          href={lang === 'en' ? '/' : `/${lang}`}
          className={'flex-shrink-0'}
        >
          <Image
            src={'/QFLogoWhite.svg'}
            alt={'QuickFacts'}
            width={156}
            height={32}
          />
        </Link>

        {menu.links?.length > 0 ? (
          <NavigationMenu.Root className="relative z-10 hidden w-screen items-center justify-center lg:flex">
            <NavigationMenu.List className="center m-0 flex list-none gap-8 p-1">
              {menu.links.map((link, index) =>
                link.children?.length ? (
                  <NavigationMenu.Item key={link.id}>
                    <NavigationMenu.Trigger
                      className={clsx(
                        'group font-inter flex items-center justify-between gap-0.5 rounded font-semibold text-white outline-none select-none',
                      )}
                    >
                      {link.label}
                      <ChevDown className="text-white" />
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content className="data-[motion=from-end]:animate-enterFromRight data-[motion=from-start]:animate-enterFromLeft data-[motion=to-end]:animate-exitToRight data-[motion=to-start]:animate-exitToLeft absolute top-full left-1/2 mt-2 -translate-x-[calc(50%+42px)] sm:w-auto">
                      <div className="shadow-purple-combined m-0 w-screen max-w-[1160px] list-none overflow-hidden rounded-2xl">
                        <SubMenu
                          links={link.children}
                          title={link.dropdown_title || link.label}
                          featuredBlocks={menu.featuredBlocks}
                          featuredBlockTitle={menu.featuredBlockTitle}
                        />
                      </div>
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                ) : (
                  <NavigationMenu.Item asChild={true} key={index}>
                    <li>
                      <Link
                        className={clsx(
                          'font-inter font-semibold',
                          'text-white',
                        )}
                        href={
                          generateLink(lang, link.type, link.slug) ?? link.slug
                        }
                      >
                        {link.label}
                      </Link>
                    </li>
                  </NavigationMenu.Item>
                ),
              )}
            </NavigationMenu.List>
          </NavigationMenu.Root>
        ) : null}

        <div className="font-inter ml-auto flex items-center gap-4 text-sm">
          {menu.cta && menu.cta.text && menu.cta.target?.length > 0 && (
            <Link
              href={
                generateLink(
                  lang,
                  menu.cta.target[0].post_type,
                  menu.cta.target[0].post_name,
                ) ?? '#'
              }
              className={clsx(
                'hidden h-10 items-center rounded px-6 font-semibold whitespace-nowrap transition-colors lg:flex',
                isScrolled
                  ? 'bg-teal-600 text-white hover:bg-white hover:text-teal-600'
                  : 'bg-teal-600 text-white hover:bg-white hover:text-teal-600',
              )}
            >
              {menu.cta.text}
            </Link>
          )}
          <Link
            href={process.env.NEXT_PUBLIC_APP_URL ?? '/'}
            className={clsx(
              'hover:text-qf-purple hidden h-10 items-center rounded border border-white bg-transparent px-6 font-semibold whitespace-nowrap text-white transition-colors hover:bg-white lg:flex',
            )}
          >
            {loginText[lang].login}
          </Link>
          <div
            className={clsx(
              'gap-1',
              isMobileMenuOpen ? 'flex lg:hidden' : 'hidden lg:flex',
            )}
          >
            <Link
              href={'/'}
              className={`flex h-7 w-8 items-center justify-center rounded border border-[rgba(255,255,255,0.4)] text-sm ${lang === 'en' ? 'bg-[rgba(255,255,255,0.4)]' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              EN
            </Link>
            <Link
              href={'/fr'}
              className={`flex h-7 w-8 items-center justify-center rounded border border-[rgba(255,255,255,0.4)] text-sm ${lang === 'fr' ? 'bg-[rgba(255,255,255,0.4)]' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              FR
            </Link>
          </div>
          {/* Mobile Menu Button */}
          <button
            className="relative flex h-8 w-8 items-center justify-center lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={
              isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'
            }
          >
            {isMobileMenuOpen ? (
              <CloseIcon className={clsx('h-6 w-6 text-white')} />
            ) : (
              <HamburgerMenu className={clsx('h-6 w-6 text-white')} />
            )}
          </button>
        </div>
      </ContentContainer>

      <MobileMenu
        lang={lang}
        menu={menu}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </header>
  );
}
